import shipGlb from 'assets/glb/Ship.glb';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Mode } from 'types';

// --- Orbit parameters ---
const ORBIT_RADIUS = 60;
const ORBIT_HEIGHT = 15;
const ORBIT_SPEED = 0.075; // was 0.15
const ORBIT_ELLIPSE_FACTOR = 0.7;
const ORBIT_RETURN_DURATION = 2500; // ms — smooth fly-back after exiting pilot mode

// --- Autopilot bounds (match original: ship flies above stars, never through them) ---
const AUTOPILOT_MAX_RADIUS = 70;
const AUTOPILOT_MIN_HEIGHT = 8;
const AUTOPILOT_MAX_HEIGHT = 35;

// --- Camera follow constants ---
const FOLLOW_DISTANCE_DEFAULT = 30;
const FOLLOW_HEIGHT_DEFAULT = 18;
const FOLLOW_DISTANCE_MIN = 15;
const FOLLOW_DISTANCE_MAX = 80;
const TRANSITION_DURATION = 1500;

// --- Input constants ---
const BOOST_DELAY = 300;

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const HEX_CHARS = '0123456789abcdef';
function generateRandomWalletAddress(): string {
  let addr = '0x';
  for (let i = 0; i < 40; i++) addr += HEX_CHARS[Math.floor(Math.random() * 16)];
  return addr;
}

/** Wallet from the actual whitelist (loaded into the engine from /stars). */
export interface KnownWallet {
  address: string;
  roi: number;
}

interface EngineData {
  light: THREE.PointLight;
  particles: THREE.Points;
  velocities: Float32Array;
  lifetimes: Float32Array;
  basePos: { x: number; y: number; z: number };
  scale: number;
  baseParticleCount: number;
  totalParticleCount: number;
}

/**
 * Spaceship rendered in the main galaxy scene on layer 1 (no bloom).
 * Autopilot navigates autonomously through choosing → turning → flying → scanning states.
 * setFollowMode(true) makes the camera follow the ship.
 */
export class SpaceshipSystem {
  private ship: THREE.Group | null = null;

  // Orbit state (used when not in follow mode)
  private orbitAngle = 0;
  private orbitReturnActive = false;
  private orbitReturnStartTime = 0;
  private orbitReturnStartPos = new THREE.Vector3();

  // Ship mode
  private mode: Mode = 'main';

  // Camera transition (entry only — exit just re-enables controls, no animation)
  private cameraTransitioning = false;
  private transitionStartTime = 0;
  private transitionStartPos = new THREE.Vector3();
  private transitionStartTarget = new THREE.Vector3();

  // Follow camera config (adjustable by wheel zoom)
  private followDistance = FOLLOW_DISTANCE_DEFAULT;
  private followHeight = FOLLOW_HEIGHT_DEFAULT;

  // Camera orbit during scanning
  private cameraOrbitActive = false;
  private cameraOrbitCurrentAngle = 0;
  private cameraOrbitStartAngle = 0;
  private cameraOrbitTargetAngle = 0;
  private cameraOrbitArc = 0;
  private cameraOrbitDirection = 1;
  private cameraOrbitProgress = 0;
  private cameraOrbitDuration = 2;
  private scanCountSinceLastOrbit = 0;
  private nextOrbitAfterScans = 3;

  // Autopilot state machine
  private autopilotState: 'choosing' | 'turning' | 'flying' | 'scanning' = 'choosing';
  private autopilotStateTimer = 0;
  private autopilotCurrentPos = new THREE.Vector3();
  private autopilotCurrentDir = new THREE.Vector3(0, 0, -1); // synced from ship.rotation on entry
  private autopilotTargetDir = new THREE.Vector3(0, 0, -1);
  private autopilotTarget = new THREE.Vector3();
  private autopilotFlightSpeed = 0.3;
  private timeSinceLastScan = 0;
  private currentScanDuration = 2;
  // Rescan mode (quick 1s rescan after 5-8 normal scans)
  private isRescanMode = false;
  private scanCountSinceLastRescan = 0;
  private nextRescanAfterScans = 5 + Math.floor(Math.random() * 4);

  // Engine particles
  private plasmaEngines: EngineData[] = [];
  private topEngines: EngineData[] = [];
  private shipBoostActive = false;

  // Scanner beam
  private scannerBeam: THREE.Group | null = null;

  // Lazy engine/scanner init — created on first pilot/autopilot activation to save startup memory
  private enginesReady = false;
  private pendingEnginesShipGroup: THREE.Group | null = null;
  private pendingEnginesScale = 1.0;

  // Callback invoked when scan completes (only in autopilot mode)
  private onScanComplete: ((wallets: { address: string; roi: number }[]) => void) | null = null;

  // Whitelist of real wallets fed in from the engine via setKnownWallets.
  // The autopilot scan picks 1-5 random entries from this pool to emit as
  // "discovered wallets". Falls back to synthetic random hex addresses
  // only if the pool is empty (e.g. before /stars finishes loading).
  private knownWallets: KnownWallet[] = [];

  // Input handlers
  private pointerDownHandler: ((e: PointerEvent) => void) | null = null;
  private pointerMoveHandler: ((e: PointerEvent) => void) | null = null;
  private pointerUpHandler: (() => void) | null = null;
  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private isDragging = false;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;

  // Temp vectors
  private tmpOrbitPos = new THREE.Vector3();
  private tmpOrbitNext = new THREE.Vector3();

  constructor(
    private container: HTMLDivElement,
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    _renderer: THREE.WebGLRenderer,
    private controls: OrbitControls
  ) {
    this.loadModel();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Called each frame by AnimationLoop via SpaceEngine.onTick */
  tick(dt: number): void {
    if (!this.ship) return;

    if (this.mode !== 'main') {
      this.animateAutopilot(dt);
      this.updateCameraOrbit(dt);
      if (this.cameraTransitioning) {
        this.updateEntryTransition(performance.now());
      } else {
        this.updateCameraFollow();
      }
      this.animatePlasmaEngines(dt);
    } else {
      this.orbitAngle -= ORBIT_SPEED * dt;
      if (this.orbitReturnActive) {
        this.updateOrbitReturn();
      } else {
        this.updateOrbit(this.ship, this.orbitAngle);
      }
    }
  }

  setMode(mode: Mode): void {
    if (mode === this.mode) return;
    const prevMode = this.mode;
    this.mode = mode;

    if (mode !== 'main' && prevMode === 'main') {
      // orbit → pilot/autopilot: enter follow mode — init engines on first activation
      this.ensureEnginesSetup();
      if (this.ship) {
        this.autopilotCurrentPos.copy(this.ship.position);
        const entryYaw = this.ship.rotation.y;
        this.autopilotCurrentDir.set(Math.sin(entryYaw), 0, Math.cos(entryYaw));
        this.autopilotTargetDir.copy(this.autopilotCurrentDir);
      }
      this.autopilotState = 'choosing';
      this.autopilotStateTimer = 0;
      this.timeSinceLastScan = 0;
      this.isRescanMode = false;
      this.scanCountSinceLastRescan = 0;
      this.nextRescanAfterScans = 5 + Math.floor(Math.random() * 4);
      this.cameraOrbitActive = false;
      this.cameraOrbitCurrentAngle = 0;
      this.cameraOrbitProgress = 0;
      this.scanCountSinceLastOrbit = 0;
      this.nextOrbitAfterScans = 3 + Math.floor(Math.random() * 3);
      this.followDistance = FOLLOW_DISTANCE_DEFAULT;
      this.followHeight = FOLLOW_HEIGHT_DEFAULT;
      this.controls.autoRotate = false;
      this.controls.enableRotate = false;
      this.controls.enablePan = false;
      this.cameraTransitioning = true;
      this.transitionStartTime = performance.now();
      this.transitionStartPos.copy(this.camera.position);
      this.transitionStartTarget.copy(this.controls.target);
      this.attachInputHandlers();
    } else if (mode === 'main') {
      // pilot/autopilot → orbit: exit follow mode
      this.orbitAngle = Math.atan2(this.autopilotCurrentPos.z, this.autopilotCurrentPos.x);
      if (this.scannerBeam) this.scannerBeam.visible = false;
      if (this.ship) {
        this.orbitReturnStartPos.copy(this.ship.position);
        this.orbitReturnStartTime = performance.now();
        this.orbitReturnActive = true;
      }
      this.cameraTransitioning = false;
      this.controls.autoRotate = false;
      this.controls.enableRotate = true;
      this.controls.enablePan = true;
      if (this.ship) this.controls.target.copy(this.ship.position);
      this.followDistance = FOLLOW_DISTANCE_DEFAULT;
      this.followHeight = FOLLOW_HEIGHT_DEFAULT;
      this.detachInputHandlers();
    } else if (mode === 'pilot' && prevMode === 'autopilot') {
      // autopilot → pilot: hide engines and scanner
      this.plasmaEngines.forEach((e) => {
        e.light.visible = false;
        e.particles.visible = false;
      });
      this.topEngines.forEach((e) => {
        e.light.visible = false;
        e.particles.visible = false;
      });
      if (this.scannerBeam) this.scannerBeam.visible = false;
    }
    // pilot → autopilot: animatePlasmaEngines handles visibility automatically
  }

  resetMode(): void {
    this.setMode('main');
  }

  isOrbit(): boolean {
    return this.mode === 'main';
  }

  setScanCompleteCallback(cb: (wallets: { address: string; roi: number }[]) => void): void {
    this.onScanComplete = cb;
  }

  /**
   * Provide the whitelist of real wallets the autopilot can "discover"
   * during scans. Called by SpaceEngine.setStars after /stars loads.
   * Subsequent calls fully replace the previous pool.
   */
  setKnownWallets(wallets: KnownWallet[]): void {
    this.knownWallets = wallets;
  }

  getShipGroup(): THREE.Group | null {
    return this.ship;
  }

  destroy(): void {
    this.detachInputHandlers();

    if (this.ship) {
      this.scene.remove(this.ship);
      this.ship.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          const mat = child.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat.dispose();
        }
      });
    }

    if (this.scannerBeam) {
      this.scene.remove(this.scannerBeam);
    }

    // Release pending reference if engines were never initialized
    this.pendingEnginesShipGroup = null;
  }

  private ensureEnginesSetup(): void {
    if (this.enginesReady || !this.pendingEnginesShipGroup) return;
    this.setupEnginesAndBeam(this.pendingEnginesShipGroup, this.pendingEnginesScale);
    this.pendingEnginesShipGroup = null;
    this.enginesReady = true;
  }

  // ── Private: Model Loading ─────────────────────────────────────────────────

  private loadModel(): void {
    const loader = new GLTFLoader();

    const setupShip = (shipGroup: THREE.Group) => {
      this.ship = shipGroup;

      this.scene.add(shipGroup);

      // Start at orbit position
      this.calculateOrbitPosition(this.orbitAngle, this.tmpOrbitPos);
      shipGroup.position.copy(this.tmpOrbitPos);
      this.autopilotCurrentPos.copy(this.tmpOrbitPos);
      this.autopilotCurrentDir.set(-1, 0, 0);
    };

    loader.load(
      shipGlb,
      (gltf) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        model.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 8 / maxDim;
        model.scale.setScalar(scale);

        const shipGroup = new THREE.Group();
        shipGroup.add(model);

        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (child.material) {
              const mats = Array.isArray(child.material) ? child.material : [child.material];
              mats.forEach((mat) => enhanceShipMaterial(mat));
            }
          }
        });

        setupSpaceshipLighting(shipGroup, model, scale);
        this.pendingEnginesShipGroup = shipGroup;
        this.pendingEnginesScale = scale;
        setupShip(shipGroup);
      },
      undefined,
      () => {
        const shipGroup = createProceduralSpaceship();
        const proceduralModel = shipGroup.children[0];
        setupSpaceshipLighting(shipGroup, proceduralModel, 1.0);
        this.pendingEnginesShipGroup = shipGroup;
        this.pendingEnginesScale = 1.0;
        shipGroup.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (child.material) {
              const mats = Array.isArray(child.material) ? child.material : [child.material];
              mats.forEach((mat) => enhanceShipMaterial(mat));
            }
          }
        });
        setupShip(shipGroup);
      }
    );
  }

  private setupEnginesAndBeam(shipGroup: THREE.Group, scale: number): void {
    // === PLASMA ENGINES (yellow-red) ===
    const enginePositions = [
      { x: 1.8 * scale, y: 0.1 * scale, z: -2.5 * scale },
      { x: -1.8 * scale, y: 0.1 * scale, z: -2.5 * scale },
    ];

    enginePositions.forEach((pos) => {
      const plasmaLight = new THREE.PointLight(0xff6600, 5, 20);
      plasmaLight.position.set(pos.x, pos.y, pos.z);
      plasmaLight.visible = false;
      shipGroup.add(plasmaLight);

      const particleCount = 900;
      const baseParticleCount = 600;
      const geom = new THREE.BufferGeometry();
      const posArr = new Float32Array(particleCount * 3);
      const colArr = new Float32Array(particleCount * 3);
      const velArr = new Float32Array(particleCount * 3);
      const lifetimes = new Float32Array(particleCount);

      for (let i = 0; i < particleCount; i++) {
        posArr[i * 3] = pos.x + (Math.random() - 0.5) * 0.3 * scale;
        posArr[i * 3 + 1] = pos.y + (Math.random() - 0.5) * 0.3 * scale;
        posArr[i * 3 + 2] = pos.z - Math.random() * 3 * scale;
        const t = Math.random();
        colArr[i * 3] = 1.0;
        colArr[i * 3 + 1] = 0.3 + t * 0.5;
        colArr[i * 3 + 2] = t * 0.2;
        velArr[i * 3] = (Math.random() - 0.5) * 0.015;
        velArr[i * 3 + 1] = (Math.random() - 0.5) * 0.015;
        velArr[i * 3 + 2] = -0.05 - Math.random() * 0.08;
        lifetimes[i] = Math.random();
      }

      geom.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
      geom.setAttribute('color', new THREE.BufferAttribute(colArr, 3));

      const mat = new THREE.PointsMaterial({
        size: 0.015 * scale,
        transparent: true,
        opacity: 0.4,
        vertexColors: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: true,
      });

      const particles = new THREE.Points(geom, mat);
      particles.visible = false;
      shipGroup.add(particles);

      this.plasmaEngines.push({
        light: plasmaLight,
        particles,
        velocities: velArr,
        lifetimes,
        basePos: pos,
        scale,
        baseParticleCount,
        totalParticleCount: particleCount,
      });
    });

    // === TOP BLUE MANEUVERING ENGINES ===
    const topPositions = [
      { x: 0.55 * scale, y: 1.45 * scale, z: -1 * scale },
      { x: -0.55 * scale, y: 1.45 * scale, z: -1 * scale },
    ];

    topPositions.forEach((pos) => {
      const topLight = new THREE.PointLight(0x00aaff, 2, 10);
      topLight.position.set(pos.x, pos.y, pos.z);
      topLight.visible = false;
      shipGroup.add(topLight);

      const particleCount = 900;
      const baseParticleCount = 600;
      const geom = new THREE.BufferGeometry();
      const posArr = new Float32Array(particleCount * 3);
      const colArr = new Float32Array(particleCount * 3);
      const velArr = new Float32Array(particleCount * 3);
      const lifetimes = new Float32Array(particleCount);

      for (let i = 0; i < particleCount; i++) {
        posArr[i * 3] = pos.x + (Math.random() - 0.5) * 0.15 * scale;
        posArr[i * 3 + 1] = pos.y + (Math.random() - 0.5) * 0.15 * scale;
        posArr[i * 3 + 2] = pos.z - Math.random() * 1.0 * scale;
        const t = Math.random();
        colArr[i * 3] = 0.2 + t * 0.3;
        colArr[i * 3 + 1] = 0.6 + t * 0.4;
        colArr[i * 3 + 2] = 1.0;
        velArr[i * 3] = (Math.random() - 0.5) * 0.008;
        velArr[i * 3 + 1] = (Math.random() - 0.5) * 0.008;
        velArr[i * 3 + 2] = -0.02 - Math.random() * 0.03;
        lifetimes[i] = Math.random();
      }

      geom.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
      geom.setAttribute('color', new THREE.BufferAttribute(colArr, 3));

      const mat = new THREE.PointsMaterial({
        size: 0.004 * scale,
        transparent: true,
        opacity: 0.5,
        vertexColors: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: true,
      });

      const particles = new THREE.Points(geom, mat);
      particles.visible = false;
      shipGroup.add(particles);

      this.topEngines.push({
        light: topLight,
        particles,
        velocities: velArr,
        lifetimes,
        basePos: pos,
        scale,
        baseParticleCount,
        totalParticleCount: particleCount,
      });
    });

    // === SCANNER BEAM (fan of 100 green rays) ===
    const beamGroup = new THREE.Group();
    const rayCount = 100;
    const fanAngle = (35 * Math.PI) / 180;
    const baseRayLength = 1;

    for (let i = 0; i < rayCount; i++) {
      const t = i / (rayCount - 1);
      const angle = -fanAngle / 2 + t * fanAngle;

      const rayGeom = new THREE.CylinderGeometry(0.0011, 0.0011, baseRayLength, 4);
      const rayMat = new THREE.MeshBasicMaterial({
        color: 0x00ff44,
        transparent: true,
        opacity: 0.4 + Math.random() * 0.3,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });

      const ray = new THREE.Mesh(rayGeom, rayMat);
      ray.position.set(
        (Math.sin(angle) * baseRayLength) / 2,
        (-Math.cos(angle) * baseRayLength) / 2,
        0
      );
      ray.rotation.z = angle;
      beamGroup.add(ray);
    }

    const scanLight = new THREE.PointLight(0x00ff66, 3, 25);
    scanLight.position.set(0, 0, 0);
    beamGroup.add(scanLight);

    beamGroup.visible = false;
    this.scene.add(beamGroup);
    this.scannerBeam = beamGroup;
  }

  // ── Private: Autopilot ─────────────────────────────────────────────────────

  private animateAutopilot(dt: number): void {
    // Autopilot only runs in autopilot mode
    if (this.mode !== 'autopilot') return;

    const ship = this.ship!;
    this.autopilotStateTimer += dt;
    this.timeSinceLastScan += dt;

    switch (this.autopilotState) {
      case 'choosing': {
        // Pick a target above stars (minHeight=8 ensures ship never flies through stars)
        const angle = Math.random() * Math.PI * 2;
        const radius = 20 + Math.random() * 40;
        const height =
          AUTOPILOT_MIN_HEIGHT + Math.random() * (AUTOPILOT_MAX_HEIGHT - AUTOPILOT_MIN_HEIGHT);

        this.autopilotTarget.set(Math.cos(angle) * radius, height, Math.sin(angle) * radius * 0.7);

        this.autopilotTargetDir
          .copy(this.autopilotTarget)
          .sub(this.autopilotCurrentPos)
          .normalize();

        this.autopilotFlightSpeed = 0.3 + Math.random() * 0.4;
        this.autopilotState = 'turning';
        this.autopilotStateTimer = 0;
        break;
      }

      case 'turning': {
        this.autopilotCurrentDir.lerp(this.autopilotTargetDir, 0.03).normalize();
        this.autopilotFlightSpeed = 0.1;

        const lookTarget = this.autopilotCurrentPos.clone().add(this.autopilotCurrentDir);
        ship.position.copy(this.autopilotCurrentPos);
        safeLookAt(ship, lookTarget);
        ship.rotation.z = 0;

        const dot = this.autopilotCurrentDir.dot(this.autopilotTargetDir);
        if (dot > 0.99 || this.autopilotStateTimer > 3) {
          this.autopilotState = 'flying';
          this.autopilotStateTimer = 0;
          this.autopilotFlightSpeed = 0.5 + Math.random() * 0.3;
        }
        break;
      }

      case 'flying': {
        const moveSpeed = this.autopilotFlightSpeed * dt * 60;
        this.autopilotCurrentPos.addScaledVector(this.autopilotCurrentDir, moveSpeed);

        // Keep within galaxy bounds
        const distFromCenter = Math.sqrt(
          this.autopilotCurrentPos.x ** 2 + this.autopilotCurrentPos.z ** 2
        );
        if (distFromCenter > AUTOPILOT_MAX_RADIUS) {
          this.autopilotCurrentPos.x *= AUTOPILOT_MAX_RADIUS / distFromCenter;
          this.autopilotCurrentPos.z *= AUTOPILOT_MAX_RADIUS / distFromCenter;
          this.autopilotState = 'choosing';
          this.autopilotStateTimer = 0;
          break;
        }
        // Clamp height — ship always flies above stars
        this.autopilotCurrentPos.y = Math.max(
          AUTOPILOT_MIN_HEIGHT,
          Math.min(AUTOPILOT_MAX_HEIGHT, this.autopilotCurrentPos.y)
        );

        const flyTarget = this.autopilotCurrentPos.clone().add(this.autopilotCurrentDir);
        ship.position.copy(this.autopilotCurrentPos);
        safeLookAt(ship, flyTarget);
        ship.rotation.z = Math.sin(this.autopilotStateTimer * 2) * 0.1;

        const distToTarget = this.autopilotCurrentPos.distanceTo(this.autopilotTarget);
        const shouldScan = this.timeSinceLastScan > 8 + Math.random() * 7;

        if (distToTarget < 5 || this.autopilotStateTimer > 8 || shouldScan) {
          this.autopilotState = 'scanning';
          this.autopilotStateTimer = 0;
          this.autopilotFlightSpeed = 0;
          this.timeSinceLastScan = 0;
          this.currentScanDuration = 2 + Math.random() * 2;
          this.isRescanMode = false;

          if (this.scannerBeam) {
            this.scannerBeam.position.copy(this.autopilotCurrentPos);
            this.scannerBeam.visible = true;
          }

          // Trigger camera orbit effect every few scans
          if (!this.cameraOrbitActive) {
            this.scanCountSinceLastOrbit++;
            if (this.scanCountSinceLastOrbit >= this.nextOrbitAfterScans) {
              this.startCameraOrbit();
              this.scanCountSinceLastOrbit = 0;
              this.nextOrbitAfterScans = 3 + Math.floor(Math.random() * 3);
            }
          }
        }
        break;
      }

      case 'scanning': {
        ship.position.copy(this.autopilotCurrentPos);
        this.autopilotFlightSpeed = 0;

        // Keep ship level (horizontal) while scanning
        const levelDir = this.autopilotCurrentDir.clone();
        levelDir.y = 0;
        if (levelDir.lengthSq() < 0.0001) levelDir.set(1, 0, 0);
        levelDir.normalize();
        safeLookAt(ship, this.autopilotCurrentPos.clone().add(levelDir));
        ship.rotation.z = 0;

        // Animate scanner beam
        if (this.scannerBeam) {
          this.scannerBeam.position.copy(this.autopilotCurrentPos);
          const shipHeight = this.autopilotCurrentPos.y;
          const rayScale = shipHeight + 8; // Reach just past star level with small overshoot
          this.scannerBeam.scale.set(rayScale, rayScale, rayScale);
          const shipYaw = Math.atan2(levelDir.x, levelDir.z);
          this.scannerBeam.rotation.set(0, shipYaw, 0);

          let rayIndex = 0;
          this.scannerBeam.traverse((child) => {
            if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshBasicMaterial) {
              const wave = Math.sin(this.autopilotStateTimer * 8 + rayIndex * 0.1) * 0.3;
              child.material.opacity = 0.3 + wave;
              rayIndex++;
            }
            if (child instanceof THREE.PointLight) {
              child.intensity = 2 + Math.sin(this.autopilotStateTimer * 6) * 1;
            }
          });
        }

        if (this.autopilotStateTimer > this.currentScanDuration) {
          if (this.scannerBeam) this.scannerBeam.visible = false;

          // Emit wallets for normal scans (not quick rescans)
          if (!this.isRescanMode && this.onScanComplete) {
            const count = 1 + Math.floor(Math.random() * 5);
            const wallets: { address: string; roi: number }[] = [];

            if (this.knownWallets.length > 0) {
              // Sample without replacement from the real whitelist so we
              // don't show the same address twice in a single scan batch.
              const pool = this.knownWallets;
              const pickCount = Math.min(count, pool.length);
              const usedIndices = new Set<number>();
              while (wallets.length < pickCount) {
                const idx = Math.floor(Math.random() * pool.length);
                if (usedIndices.has(idx)) continue;
                usedIndices.add(idx);
                wallets.push({ address: pool[idx].address, roi: pool[idx].roi });
              }
            } else {
              // Whitelist not loaded yet (very early in the session, before
              // /stars resolves). Keep the historical random-hex behavior so
              // the UI doesn't go silent — the user will only ever see this
              // for a few seconds at most.
              for (let i = 0; i < count; i++) {
                wallets.push({
                  address: generateRandomWalletAddress(),
                  roi: 50 + Math.random() * 450,
                });
              }
            }

            this.onScanComplete(wallets);
          }

          // Check if we should do a quick rescan (every 5-8 normal scans)
          if (!this.isRescanMode) {
            this.scanCountSinceLastRescan++;
            if (this.scanCountSinceLastRescan >= this.nextRescanAfterScans) {
              this.isRescanMode = true;
              this.currentScanDuration = 1; // Quick 1-second rescan
              this.autopilotStateTimer = 0;
              this.scanCountSinceLastRescan = 0;
              this.nextRescanAfterScans = 5 + Math.floor(Math.random() * 4);
              if (this.scannerBeam) this.scannerBeam.visible = true;
              break; // Stay in scanning state
            }
          } else {
            this.isRescanMode = false;
          }

          this.autopilotState = 'choosing';
          this.autopilotStateTimer = 0;
          this.currentScanDuration = 2 + Math.random() * 2;
        }
        break;
      }
    }
  }

  // ── Private: Camera Follow ─────────────────────────────────────────────────

  private updateCameraOrbit(dt: number): void {
    if (!this.cameraOrbitActive) return;

    this.cameraOrbitProgress += dt / this.cameraOrbitDuration;
    if (this.cameraOrbitProgress >= 1) {
      this.cameraOrbitProgress = 1;
      this.cameraOrbitActive = false;
      this.cameraOrbitCurrentAngle = this.cameraOrbitTargetAngle;
    } else {
      const eased = easeInOutCubic(this.cameraOrbitProgress);
      this.cameraOrbitCurrentAngle =
        this.cameraOrbitStartAngle + this.cameraOrbitArc * this.cameraOrbitDirection * eased;
    }
  }

  private updateCameraFollow(): void {
    if (!this.ship) return;

    const shipPos = this.ship.position.clone();
    const shipForward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.ship.quaternion);

    // Apply orbit angle around Y axis
    const orbitRotation = new THREE.Matrix4().makeRotationY(this.cameraOrbitCurrentAngle);
    const rotatedForward = shipForward.clone().applyMatrix4(orbitRotation);

    // Dynamic height: full at 0° (behind), zero at 180° (in front)
    let normalizedAngle = this.cameraOrbitCurrentAngle % (Math.PI * 2);
    if (normalizedAngle < 0) normalizedAngle += Math.PI * 2;
    const heightMultiplier = (1 + Math.cos(normalizedAngle)) / 2;
    const dynamicHeight = this.followHeight * heightMultiplier;

    const targetCamPos = shipPos
      .clone()
      .add(rotatedForward.multiplyScalar(this.followDistance))
      .add(new THREE.Vector3(0, dynamicHeight, 0));

    // Faster follow when camera is in front of ship
    const absAngle = Math.abs(normalizedAngle - Math.PI);
    const followSpeed = absAngle < Math.PI / 2 ? 0.12 : 0.05;

    this.camera.position.lerp(targetCamPos, followSpeed);
    this.controls.target.lerp(shipPos, 0.1);
  }

  private updateEntryTransition(now: number): void {
    if (!this.ship) return;
    const elapsed = now - this.transitionStartTime;
    const t = Math.min(elapsed / TRANSITION_DURATION, 1);
    const eased = easeInOutCubic(t);

    const shipPos = this.ship.position.clone();
    const shipForward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.ship.quaternion);
    const targetPos = shipPos
      .clone()
      .add(shipForward.multiplyScalar(this.followDistance))
      .add(new THREE.Vector3(0, this.followHeight, 0));

    this.camera.position.lerpVectors(this.transitionStartPos, targetPos, eased);
    this.controls.target.lerpVectors(this.transitionStartTarget, shipPos, eased);

    if (t >= 1) this.cameraTransitioning = false;
  }

  private startCameraOrbit(): void {
    this.cameraOrbitActive = true;
    this.cameraOrbitStartAngle = this.cameraOrbitCurrentAngle;
    this.cameraOrbitProgress = 0;

    const arcs = [Math.PI / 2, Math.PI, Math.PI * 1.5, Math.PI * 2];
    this.cameraOrbitArc = arcs[Math.floor(Math.random() * arcs.length)];
    this.cameraOrbitDirection = Math.random() < 0.5 ? 1 : -1;
    this.cameraOrbitTargetAngle =
      this.cameraOrbitStartAngle + this.cameraOrbitArc * this.cameraOrbitDirection;
    this.cameraOrbitDuration = 2 + (this.cameraOrbitArc / Math.PI) * 1.5;
  }

  // ── Private: Engine Particles ──────────────────────────────────────────────

  private animatePlasmaEngines(_dt: number): void {
    const time = performance.now() * 0.001;
    const shipCamDist = this.ship ? this.camera.position.distanceTo(this.ship.position) : 0;
    const shipFar = shipCamDist > 60;

    const isScanning = this.autopilotState === 'scanning';
    const flightSpeedFactor = Math.max(1, 1 + (this.autopilotFlightSpeed / 0.3) * 1.5);
    const speedMultiplier = this.shipBoostActive ? 3 : flightSpeedFactor;
    const effectiveSpeed = isScanning && this.mode === 'autopilot' ? 0 : speedMultiplier;

    const maxTrailLength = 10;
    const speedNormalized = Math.min(effectiveSpeed / 3, 1);
    const trailLengthMultiplier = 0.25 + 0.75 * speedNormalized;
    const animSpeedMultiplier = isScanning && this.mode === 'autopilot' ? 0.15 : speedMultiplier;

    // Main plasma engines (orange-yellow)
    this.plasmaEngines.forEach((engine, idx) => {
      if (this.mode !== 'autopilot') {
        engine.light.visible = false;
        engine.particles.visible = false;
        return;
      }
      engine.light.visible = true;
      engine.particles.visible = true;

      engine.light.intensity =
        (1.5 + 1.5 * Math.sin(time * 15 + idx)) * (0.25 + 0.75 * speedNormalized);

      const posArr = engine.particles.geometry.attributes.position.array as Float32Array;
      const colArr = engine.particles.geometry.attributes.color.array as Float32Array;
      const scale = engine.scale;
      const isBoosting = speedMultiplier > 1.5;
      const activeCount = shipFar
        ? 300
        : isBoosting
          ? engine.totalParticleCount
          : engine.baseParticleCount;
      const trailLength = maxTrailLength * trailLengthMultiplier;

      for (let i = 0; i < engine.totalParticleCount; i++) {
        const i3 = i * 3;
        if (i >= activeCount) {
          posArr[i3 + 2] = -99999;
          continue;
        }
        posArr[i3] += engine.velocities[i3] * animSpeedMultiplier;
        posArr[i3 + 1] += engine.velocities[i3 + 1] * animSpeedMultiplier;
        posArr[i3 + 2] += engine.velocities[i3 + 2] * animSpeedMultiplier;
        engine.lifetimes[i] -= 0.02 * animSpeedMultiplier;

        if (engine.lifetimes[i] <= 0 || posArr[i3 + 2] < engine.basePos.z - trailLength * scale) {
          posArr[i3] = engine.basePos.x + (Math.random() - 0.5) * 0.15 * scale;
          posArr[i3 + 1] = engine.basePos.y + (Math.random() - 0.5) * 0.15 * scale;
          posArr[i3 + 2] = engine.basePos.z;
          engine.lifetimes[i] = 0.5 + Math.random() * 0.5;
          colArr[i3] = 1.0;
          colArr[i3 + 1] = 0.6 + speedNormalized * 0.1;
          colArr[i3 + 2] = 0.2;
        } else {
          const life = engine.lifetimes[i];
          colArr[i3 + 1] = Math.max(0.1, life * 0.8);
          colArr[i3 + 2] = Math.max(0, life * 0.2);
        }
      }

      engine.particles.geometry.attributes.position.needsUpdate = true;
      engine.particles.geometry.attributes.color.needsUpdate = true;
      (engine.particles.material as THREE.PointsMaterial).size =
        (0.015 + speedNormalized * 0.005) * scale;
    });

    // Top blue maneuvering engines
    this.topEngines.forEach((engine, idx) => {
      if (this.mode !== 'autopilot') {
        engine.light.visible = false;
        engine.particles.visible = false;
        return;
      }
      engine.light.visible = true;
      engine.particles.visible = true;

      engine.light.intensity = 1.0 + 0.5 * Math.sin(time * 12 + idx * 2);

      const posArr = engine.particles.geometry.attributes.position.array as Float32Array;
      const colArr = engine.particles.geometry.attributes.color.array as Float32Array;
      const scale = engine.scale;
      const isBoosting = speedMultiplier > 1.5;
      const activeCount = shipFar
        ? 300
        : isBoosting
          ? engine.totalParticleCount
          : engine.baseParticleCount;

      for (let i = 0; i < engine.totalParticleCount; i++) {
        const i3 = i * 3;
        if (i >= activeCount) {
          posArr[i3 + 2] = -99999;
          continue;
        }
        posArr[i3] += engine.velocities[i3];
        posArr[i3 + 1] += engine.velocities[i3 + 1];
        posArr[i3 + 2] += engine.velocities[i3 + 2];
        engine.lifetimes[i] -= 0.025;

        const trailLength = 1.33;
        if (engine.lifetimes[i] <= 0 || posArr[i3 + 2] < engine.basePos.z - trailLength * scale) {
          posArr[i3] = engine.basePos.x + (Math.random() - 0.5) * 0.1 * scale;
          posArr[i3 + 1] = engine.basePos.y + (Math.random() - 0.5) * 0.1 * scale;
          posArr[i3 + 2] = engine.basePos.z;
          engine.lifetimes[i] = 0.4 + Math.random() * 0.4;
          const t = Math.random();
          colArr[i3] = 0.2 + t * 0.3;
          colArr[i3 + 1] = 0.6 + t * 0.4;
          colArr[i3 + 2] = 1.0;
        } else {
          const life = engine.lifetimes[i];
          colArr[i3] = 0.1 + life * 0.3;
          colArr[i3 + 1] = 0.4 + life * 0.4;
        }
      }

      engine.particles.geometry.attributes.position.needsUpdate = true;
      engine.particles.geometry.attributes.color.needsUpdate = true;
    });
  }

  // ── Private: Orbit (non-follow mode) ──────────────────────────────────────

  private calculateOrbitPosition(angle: number, target: THREE.Vector3): THREE.Vector3 {
    const x = Math.cos(angle) * ORBIT_RADIUS;
    const z = Math.sin(angle) * ORBIT_RADIUS * ORBIT_ELLIPSE_FACTOR;
    const y = ORBIT_HEIGHT + Math.sin(angle * 2) * 5;
    return target.set(x, y, z);
  }

  private updateOrbitReturn(): void {
    if (!this.ship) return;
    const elapsed = performance.now() - this.orbitReturnStartTime;
    const t = Math.min(elapsed / ORBIT_RETURN_DURATION, 1);
    const eased = easeInOutCubic(t);

    // Target: current orbit position (moves as orbitAngle updates each frame)
    this.calculateOrbitPosition(this.orbitAngle, this.tmpOrbitPos);
    this.ship.position.lerpVectors(this.orbitReturnStartPos, this.tmpOrbitPos, eased);

    // Face in the orbit direction throughout the return
    this.calculateOrbitPosition(this.orbitAngle - 0.01, this.tmpOrbitNext);
    safeLookAt(this.ship, this.tmpOrbitNext);
    this.ship.rotation.z = Math.sin(this.orbitAngle) * 0.15;

    if (t >= 1) this.orbitReturnActive = false;
  }

  private updateOrbit(ship: THREE.Group, angle: number): void {
    this.calculateOrbitPosition(angle, this.tmpOrbitPos);
    ship.position.copy(this.tmpOrbitPos);
    this.calculateOrbitPosition(angle - 0.01, this.tmpOrbitNext);
    safeLookAt(ship, this.tmpOrbitNext);
    ship.rotation.z = Math.sin(angle) * 0.15;
  }

  // ── Private: Input Handlers ────────────────────────────────────────────────

  private attachInputHandlers(): void {
    this.pointerDownHandler = () => {
      this.isDragging = true;
      this.pressTimer = setTimeout(() => {
        this.shipBoostActive = true;
      }, BOOST_DELAY);
    };

    this.pointerMoveHandler = (e: PointerEvent) => {
      if (!this.isDragging) return;
      if (Math.abs(e.movementX) > 5 || Math.abs(e.movementY) > 5) {
        if (this.pressTimer) {
          clearTimeout(this.pressTimer);
          this.pressTimer = null;
        }
      }
    };

    this.pointerUpHandler = () => {
      this.isDragging = false;
      this.shipBoostActive = false;
      if (this.pressTimer) {
        clearTimeout(this.pressTimer);
        this.pressTimer = null;
      }
    };

    this.wheelHandler = (e: WheelEvent) => {
      if (this.mode === 'main') return;
      e.preventDefault();
      this.followDistance = Math.max(
        FOLLOW_DISTANCE_MIN,
        Math.min(FOLLOW_DISTANCE_MAX, this.followDistance + e.deltaY * 0.05)
      );
      this.followHeight = FOLLOW_HEIGHT_DEFAULT * (this.followDistance / FOLLOW_DISTANCE_DEFAULT);
    };

    window.addEventListener('pointerdown', this.pointerDownHandler);
    window.addEventListener('pointermove', this.pointerMoveHandler);
    window.addEventListener('pointerup', this.pointerUpHandler);
    this.container.addEventListener('wheel', this.wheelHandler, { passive: false });
  }

  private detachInputHandlers(): void {
    if (this.pointerDownHandler) window.removeEventListener('pointerdown', this.pointerDownHandler);
    if (this.pointerMoveHandler) window.removeEventListener('pointermove', this.pointerMoveHandler);
    if (this.pointerUpHandler) window.removeEventListener('pointerup', this.pointerUpHandler);
    if (this.wheelHandler) this.container.removeEventListener('wheel', this.wheelHandler);
    if (this.pressTimer) clearTimeout(this.pressTimer);

    this.pointerDownHandler = null;
    this.pointerMoveHandler = null;
    this.pointerUpHandler = null;
    this.wheelHandler = null;
    this.shipBoostActive = false;
    this.isDragging = false;
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function safeLookAt(ship: THREE.Group, targetPos: THREE.Vector3): void {
  const dx = targetPos.x - ship.position.x;
  const dy = targetPos.y - ship.position.y;
  const dz = targetPos.z - ship.position.z;
  if (dx * dx + dy * dy + dz * dz < 0.0001) return;

  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const nx = dx / len;
  const ny = dy / len;
  const nz = dz / len;

  const yaw = Math.atan2(nx, nz);
  const horizontalDist = Math.sqrt(nx * nx + nz * nz);
  const pitch = Math.max(-0.8, Math.min(0.8, Math.atan2(ny, horizontalDist)));

  ship.rotation.order = 'YXZ';
  ship.rotation.y = yaw;
  ship.rotation.x = -pitch;
}

function enhanceShipMaterial(mat: THREE.Material): void {
  if (mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshPhysicalMaterial) {
    mat.metalness = Math.max(mat.metalness || 0, 0.8);
    mat.roughness = Math.min(mat.roughness || 1, 0.25);
    mat.envMapIntensity = 2.0;
    mat.emissive = new THREE.Color(0x222233);
    mat.emissiveIntensity = 0.2;
  }
}

function setupSpaceshipLighting(
  shipGroup: THREE.Group,
  _model: THREE.Object3D,
  scale: number
): void {
  // Soft ambient base
  shipGroup.add(new THREE.AmbientLight(0xffffff, 0.4));

  // Main key light from top-front (replaces SpotLight + 12 ring SpotLights + topKey DirectionalLight)
  const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
  keyLight.position.set(10, 30, 20);
  shipGroup.add(keyLight);

  // Side fill (replaces leftLight + rightLight + frontLight)
  const sideLight = new THREE.DirectionalLight(0xbbccff, 1.2);
  sideLight.position.set(-8, 5, 5);
  shipGroup.add(sideLight);

  // Bottom fill (counter-light from below-back)
  const bottomFill = new THREE.DirectionalLight(0xaadfff, 0.7);
  bottomFill.position.set(0, -20, -10);
  shipGroup.add(bottomFill);

  // Engine glow — blue
  const engineLight1 = new THREE.PointLight(0x00aaff, 3, 12);
  engineLight1.position.set(0, 0, -4 * scale);
  shipGroup.add(engineLight1);

  // Engine glow — orange
  const engineLight2 = new THREE.PointLight(0xff6600, 2, 8);
  engineLight2.position.set(0, -0.5 * scale, -3 * scale);
  shipGroup.add(engineLight2);
}

function createProceduralSpaceship(): THREE.Group {
  const shipGroup = new THREE.Group();

  const bodyGeometry = new THREE.ConeGeometry(1.5, 6, 8);
  const bodyMaterial = new THREE.MeshPhongMaterial({
    color: 0x2a4a6a,
    emissive: 0x1a2a3a,
    shininess: 80,
  });
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.rotation.x = Math.PI;
  body.position.y = -2;
  shipGroup.add(body);

  const midGeometry = new THREE.CylinderGeometry(1.5, 1.8, 3, 8);
  const midMaterial = new THREE.MeshPhongMaterial({
    color: 0x3a5a7a,
    emissive: 0x2a3a4a,
    shininess: 70,
  });
  const midSection = new THREE.Mesh(midGeometry, midMaterial);
  midSection.position.y = -5;
  shipGroup.add(midSection);

  const wingGeometry = new THREE.BoxGeometry(6, 0.3, 3);
  const wingMaterial = new THREE.MeshPhongMaterial({
    color: 0x4a6a8a,
    emissive: 0x2a3a5a,
    shininess: 60,
  });
  const leftWing = new THREE.Mesh(wingGeometry, wingMaterial);
  leftWing.position.set(-3, -4, 0);
  leftWing.rotation.z = Math.PI / 12;
  shipGroup.add(leftWing);
  const rightWing = new THREE.Mesh(wingGeometry, wingMaterial);
  rightWing.position.set(3, -4, 0);
  rightWing.rotation.z = -Math.PI / 12;
  shipGroup.add(rightWing);

  const cockpitGeometry = new THREE.SphereGeometry(1.2, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2);
  const cockpitMaterial = new THREE.MeshPhongMaterial({
    color: 0x88aacc,
    emissive: 0x446688,
    transparent: true,
    opacity: 0.6,
    shininess: 100,
  });
  const cockpit = new THREE.Mesh(cockpitGeometry, cockpitMaterial);
  cockpit.position.y = 1;
  cockpit.rotation.x = Math.PI;
  shipGroup.add(cockpit);

  const engineGeometry = new THREE.CylinderGeometry(0.6, 0.8, 2, 8);
  const engineMaterial = new THREE.MeshPhongMaterial({
    color: 0x1a2a3a,
    emissive: 0x0a1a2a,
    shininess: 90,
  });
  const leftEngine = new THREE.Mesh(engineGeometry, engineMaterial);
  leftEngine.position.set(-1, -7, 0);
  shipGroup.add(leftEngine);
  const rightEngine = new THREE.Mesh(engineGeometry, engineMaterial);
  rightEngine.position.set(1, -7, 0);
  shipGroup.add(rightEngine);

  const glowGeometry = new THREE.CircleGeometry(0.7, 16);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0x00ffff,
    transparent: true,
    opacity: 0.8,
  });
  const leftGlow = new THREE.Mesh(glowGeometry, glowMaterial);
  leftGlow.position.set(-1, -8, 0);
  leftGlow.rotation.x = Math.PI / 2;
  shipGroup.add(leftGlow);
  const rightGlow = new THREE.Mesh(glowGeometry, glowMaterial);
  rightGlow.position.set(1, -8, 0);
  rightGlow.rotation.x = Math.PI / 2;
  shipGroup.add(rightGlow);

  const antennaGeometry = new THREE.CylinderGeometry(0.1, 0.1, 2, 6);
  const antennaMaterial = new THREE.MeshPhongMaterial({
    color: 0x6a8aaa,
    emissive: 0x3a4a6a,
    shininess: 100,
  });
  const antenna = new THREE.Mesh(antennaGeometry, antennaMaterial);
  antenna.position.y = 2.5;
  shipGroup.add(antenna);

  const tipGeometry = new THREE.SphereGeometry(0.2, 8, 8);
  const tipMaterial = new THREE.MeshBasicMaterial({ color: 0xff00ff });
  const antennaTip = new THREE.Mesh(tipGeometry, tipMaterial);
  antennaTip.position.y = 3.5;
  shipGroup.add(antennaTip);

  shipGroup.scale.set(1.2, 1.2, 1.2);
  shipGroup.rotation.x = Math.PI / 2;
  return shipGroup;
}
